import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowUpRight, ArrowDownRight, MoreHorizontal,
  Clock, Wallet, Layers, RefreshCw, ChevronDown,
  ChevronLeft, ChevronRight, Download, X, Calendar
} from "lucide-react";
import { formatPrice, formatQty, cn } from "@/lib/utils";
import { useLatestValid } from '../hooks/useLatestValid';

interface Cycle {
  id: number;
  symbol: string;
  side: string;
  current_step: number;
  avg_price: number;
  total_qty: number;
  total_cost: number;
  pnl: number | null;
  opened_at: string;
  closed_at: string | null;
  bot_name: string;
  bot_symbol: string;
  bot_id: number;
  strategy_name: string;
  is_live: boolean;
  leverage: number;
  unrealized_pnl?: number;
  current_price?: number;
  roe?: number;
  dca_targets?: any[];
  use_testnet?: boolean;
  exchange?: string;
  exit_type?: string;
}

interface HistoryDashboardProps {
  onSelectBot?: (id: string) => void;
  disable_all_testnet?: boolean;
  userId?: number;
}

const fmtDateTime = (dateStr: string | null) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  const pad = (n: number) => n.toString().padStart(2, '0');

  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const hh = pad(date.getHours());
  const mm = pad(date.getMinutes());
  const ss = pad(date.getSeconds());

  return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
};

import { calculateFirstEntryNotional, fmtDate, fmtPriceForSymbol, getExchangeBadgeStyle } from './dca/DcaUtils';

// Sub-component for each open cycle row to maintain its own stale state
const OpenCycleRow = ({ cycle, onSelectBot, isPrivate }: { cycle: Cycle; onSelectBot?: (id: string) => void; isPrivate: boolean }) => {
  const isLong = (cycle.side || 'long').toLowerCase() === 'long';

  // Use hook for unrealized PnL and ROE
  const [unrealizedPnl, isStale] = useLatestValid(cycle.unrealized_pnl !== undefined ? cycle.unrealized_pnl : null);
  const [roe, isRoeStale] = useLatestValid(cycle.roe !== undefined ? cycle.roe : null);

  return (
    <TableRow className={`hover:bg-muted/10 transition-colors border-border ${isStale || isRoeStale ? 'opacity-40' : ''}`}>
      <TableCell className="py-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isLong ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
            {isLong ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          </div>
          <div className="flex flex-col">
            <button
              onClick={() => onSelectBot?.(String(cycle.bot_id))}
              className="text-left group"
            >
              <div className="font-black text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5 leading-none">
                <span className="text-[10px] text-muted-foreground font-bold">#{cycle.bot_id}</span>
                <span className={`px-1 rounded text-[8px] uppercase ${getExchangeBadgeStyle(cycle.exchange)} border-none`}>{cycle.exchange || 'binance'}</span>
                {cycle.bot_name ? (cycle.bot_name.length > 20 ? cycle.bot_name.substring(0, 20) + '...' : cycle.bot_name) : cycle.bot_symbol}
              </div>
            </button>
            <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-tight mt-0.5 flex items-center gap-1.5 leading-none">
              {!cycle.is_live ? (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[8px] h-3.5 px-1 leading-none font-black">PAPER</Badge>
              ) : cycle.use_testnet ? (
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[8px] h-3.5 px-1 leading-none font-black">TESTNET</Badge>
              ) : (
                <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[8px] h-3.5 px-1 leading-none font-black">LIVE</Badge>
              )}
              {cycle.strategy_name || 'DCA 전략'}
            </div>
            <Badge variant="outline" className={`text-[9px] py-0 h-4 w-fit mt-1 border-border ${isLong ? 'bg-success/5 text-success' : 'bg-danger/5 text-danger'}`}>
              {isLong ? 'LONG' : 'SHORT'} {cycle.leverage}x
            </Badge>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <p className="text-xs font-black text-foreground">${formatPrice(cycle.avg_price || 0)}</p>
        <p className="text-[10px] text-muted-foreground opacity-60">{cycle.bot_symbol || cycle.symbol}</p>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <p className="text-xs font-black text-foreground">{isPrivate ? '****' : `$${formatPrice(cycle.total_cost || 0, 2)}`} <span className="text-[10px] font-bold text-muted-foreground">Margin</span></p>
          <p className="text-[11px] font-bold text-primary">{isPrivate ? '****' : `$${formatPrice((cycle.total_cost || 0) * (cycle.leverage || 1), 2)}`} <span className="text-[9px] text-muted-foreground opacity-60">Notional</span></p>
          <p className="text-[10px] text-muted-foreground font-medium">{isPrivate ? '****' : formatQty(cycle.total_qty || 0)} Qty</p>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <p className={`text-xs font-black ${unrealizedPnl === null ? 'text-muted-foreground/30' : (Number(unrealizedPnl) >= 0 ? 'text-success' : 'text-danger')}`}>
            {isPrivate ? '****' : (unrealizedPnl === null ? '—' : `${Number(unrealizedPnl) >= 0 ? '+' : ''}${formatPrice(Number(unrealizedPnl), 2)} USDT`)}
          </p>
          {roe !== null && (
            <Badge className={`text-[10px] h-4 w-fit px-1 font-black ${Number(roe) >= 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
              {isPrivate ? '****' : `${Number(roe) >= 0 ? '+' : ''}${formatPrice(Number(roe), 2)}%`}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge className="bg-primary/10 text-primary border-none font-bold">
          {cycle.current_step}단계
        </Badge>
        <p className="text-[10px] text-muted-foreground mt-1">Fee: {isPrivate ? '****' : `$${formatPrice((cycle.total_cost || 0) * (cycle.leverage || 1) * 0.0004, 4)}`}</p>
      </TableCell>
      <TableCell>
        <p className="text-xs text-muted-foreground">
          {fmtDateTime(cycle.opened_at)}
        </p>
      </TableCell>
    </TableRow>
  );
}

export const HistoryDashboard: React.FC<HistoryDashboardProps> = ({ onSelectBot, disable_all_testnet, userId }) => {
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);
  const MODE_KEY = 'history_dashboard_mode';
  const BOT_KEY = 'history_dashboard_bot';
  const TAB_KEY = 'history_dashboard_tab';
  const MONTH_KEY = 'history_dashboard_month';

  const [openCycles, setOpenCycles] = useState<Cycle[]>([]);
  const [closedCycles, setClosedCycles] = useState<Cycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'all' | 'mainnet' | 'testnet' | 'paper'>(() => {
    const saved = localStorage.getItem(MODE_KEY);
    if (saved === 'real') return 'mainnet';
    return (saved as any) || 'all';
  });
  const [selectedBot, setSelectedBot] = useState<string>(() => localStorage.getItem(BOT_KEY) || 'all');
  const [activeTab, setActiveTab] = useState<string>(() => localStorage.getItem(TAB_KEY) || 'positions');
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const saved = localStorage.getItem(MONTH_KEY);
    if (saved) return saved;
    // 기본값을 'all'에서 이번 달(YYYY-MM)로 변경
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });

  // Pagination states
  const [historiesTotal, setHistoriesTotal] = useState(0);
  const [historiesPage, setHistoriesPage] = useState(1);
  const [historiesPer, setHistoriesPer] = useState(30);
  const [totalStats, setTotalStats] = useState({ pnl: 0, win_count: 0, total_count: 0 });

  // Export states
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportYear, setExportYear] = useState(() => String(new Date().getFullYear()));
  const [exportMonth, setExportMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));

  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
    localStorage.setItem(BOT_KEY, selectedBot);
    localStorage.setItem(TAB_KEY, activeTab);
    localStorage.setItem(MONTH_KEY, selectedMonth);
  }, [mode, selectedBot, activeTab, selectedMonth]);

  const fetchData = async (p = historiesPage, isSilent = false) => {
    if (!navigator.onLine) return;
    if (!isSilent) setLoading(true);
    try {
      const res = await fetch(`/dca/all_cycles?mode=${mode}&bot_id=${selectedBot}&page=${p}&per=${historiesPer}&month=${selectedMonth}${userId ? `&user_id=${userId}` : ''}`);
      const data = await res.json();
      setOpenCycles(data.open_cycles || []);
      setClosedCycles(data.closed_cycles || []);
      setHistoriesTotal(data.histories_total || 0);
      setHistoriesPage(data.histories_page || p);
      setHistoriesPer(data.histories_per || 50);
      setTotalStats(data.total_stats || { pnl: 0, win_count: 0, total_count: 0 });
    } catch (e) {
      if (navigator.onLine) {
        console.error('Failed to fetch cycle data', e);
      }
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  useEffect(() => {
    setHistoriesPage(1);
    fetchData(1);
  }, [mode, selectedBot, selectedMonth]);

  useEffect(() => {
    const timer = setInterval(() => fetchData(historiesPage, true), 12000);
    return () => clearInterval(timer);
  }, [mode, selectedBot, historiesPage]);

  const botOptions = Array.from(
    new Map(
      [...openCycles, ...closedCycles].map((cycle) => [
        String(cycle.bot_id),
        {
          id: String(cycle.bot_id),
          name: cycle.bot_name || cycle.bot_symbol || `Bot #${cycle.bot_id}`,
          symbol: cycle.bot_symbol || cycle.symbol || '',
        }
      ])
    ).values()
  ).sort((a, b) => Number(a.id) - Number(b.id));

  const filteredOpenCycles = openCycles;
  const filteredClosedCycles = closedCycles;
  const displayedClosed = closedCycles;

  const totalPnl = totalStats.pnl;
  const winCount = totalStats.win_count;
  const winRate = totalStats.total_count > 0
    ? ((winCount / totalStats.total_count) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 transition-colors duration-300">
      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-card p-6 rounded-3xl border border-border shadow-sm">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-foreground">거래 대시보드</h2>
          <p className="text-muted-foreground text-sm font-medium">포지션 및 거래 이력을 실시간으로 관리합니다.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="w-full sm:w-[240px]">
            <Select value={selectedBot} onValueChange={setSelectedBot}>
              <SelectTrigger className="rounded-xl border-border bg-background font-bold">
                <SelectValue placeholder="봇 선택" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체 봇</SelectItem>
                {botOptions.map((bot) => (
                  <SelectItem key={bot.id} value={bot.id}>
                    #{bot.id} {bot.name}{bot.symbol ? ` (${bot.symbol})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 bg-muted/50 p-1 rounded-2xl border border-border">
            <Button
              variant={selectedMonth === 'all' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-xl font-bold text-[10px] h-8 px-4"
              onClick={() => setSelectedMonth('all')}
            >
              전체 기간
            </Button>
            <div className={cn(
              "flex items-center gap-2 rounded-xl border border-transparent px-3 h-8 transition-all",
              selectedMonth !== 'all' ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:bg-muted"
            )}>
              <Calendar className="w-3.5 h-3.5" />
              <input
                type="month"
                value={selectedMonth === 'all' ? new Date().toISOString().substring(0, 7) : selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-transparent text-[10px] font-bold outline-none w-24 cursor-pointer"
              />
            </div>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-2xl border border-border max-w-full overflow-x-auto no-scrollbar">
            <Button
              variant={mode === 'all' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-xl font-bold text-[10px] h-8 px-4 shrink-0"
              onClick={() => setMode('all')}
            >
              전체
            </Button>
            <Button
              variant={mode === 'mainnet' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-xl font-bold text-[10px] h-8 px-4 shrink-0"
              onClick={() => setMode('mainnet')}
            >
              실전
            </Button>
            {!disable_all_testnet && (
              <Button
                variant={mode === 'testnet' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-xl font-bold text-[10px] h-8 px-4 shrink-0"
                onClick={() => setMode('testnet')}
              >
                테스트넷
              </Button>
            )}
            <Button
              variant={mode === 'paper' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-xl font-bold text-[10px] h-8 px-4 shrink-0"
              onClick={() => setMode('paper')}
            >
              시뮬레이션
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl border-border bg-background"
            onClick={() => setIsExportModalOpen(true)}
          >
            <Download className="w-4 h-4 mr-2" />
            내보내기
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl border-border bg-background"
            onClick={() => fetchData()}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </Button>
        </div>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">오픈 포지션</p>
          <p className="text-xl sm:text-3xl font-black text-primary mt-1">{filteredOpenCycles.length}</p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-3 sm:p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">누적 PnL</p>
          <p className={`text-normal sm:text-3xl font-black mt-1 ${totalPnl >= 0 ? 'text-success' : 'text-danger'}`}>
            {isPrivate ? '****' : `${totalPnl >= 0 ? '+' : ''}${formatPrice(totalPnl, 2)}`} <span className="text-[10px] sm:text-sm opacity-50">USDT</span>
          </p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">승률</p>
          <p className="text-xl sm:text-3xl font-black text-foreground mt-1">{winRate}%</p>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-transparent space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-2xl w-full h-auto flex flex-wrap justify-start border border-border">
          <TabsTrigger value="positions" className="flex-1 rounded-xl py-2.5 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-all text-muted-foreground font-bold whitespace-nowrap">
            <Wallet className="w-4 h-4 mr-2 hidden sm:inline" />
            포지션
            <Badge className="ml-2 bg-primary/10 text-primary border-none px-1.5 h-4 text-[10px]">{filteredOpenCycles.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="executions" className="flex-1 rounded-xl py-2.5 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-all text-muted-foreground font-bold whitespace-nowrap">
            <Clock className="w-4 h-4 mr-2 hidden sm:inline" />
            체결 이력
            <Badge className="ml-2 bg-foreground/10 text-foreground border-none px-1.5 h-4 text-[10px]">{filteredClosedCycles.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* 오픈 포지션 탭 */}
        <TabsContent value="positions" className="mt-0 ring-offset-background focus-visible:outline-none">
          {loading ? (
            <div className="bg-card rounded-2xl p-12 text-center text-muted-foreground">데이터 로딩 중...</div>
          ) : filteredOpenCycles.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-12 flex flex-col items-center text-center gap-3">
              <Layers className="w-10 h-10 text-muted-foreground/30" />
              <p className="text-muted-foreground font-medium">현재 오픈된 포지션이 없습니다.</p>
            </div>
          ) : (
            <Card className="border border-border shadow-xl rounded-2xl overflow-hidden bg-card">
              <Table>
                <TableHeader className="bg-muted/30 border-b border-border">
                  <TableRow className="border-border">
                    <TableHead className="font-bold text-muted-foreground">봇 / 종목</TableHead>
                    <TableHead className="font-bold text-muted-foreground">평단가</TableHead>
                    <TableHead className="font-bold text-muted-foreground">진입비용 / 포지션 / 수량</TableHead>
                    <TableHead className="font-bold text-muted-foreground">미실현 PnL (ROE%)</TableHead>
                    <TableHead className="font-bold text-muted-foreground">단계 / 수수료</TableHead>
                    <TableHead className="font-bold text-muted-foreground">시작 시간</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOpenCycles.map((cycle) => (
                    <OpenCycleRow
                      key={cycle.id}
                      cycle={cycle}
                      onSelectBot={onSelectBot}
                      isPrivate={isPrivate}
                    />
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* 체결 이력 탭 */}
        <TabsContent value="executions" className="mt-0">
          {loading ? (
            <div className="bg-card rounded-2xl p-12 text-center text-muted-foreground">데이터 로딩 중...</div>
          ) : filteredClosedCycles.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-12 text-center">
              <p className="text-muted-foreground font-medium text-sm">체결된 거래 이력이 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <Card className="border border-border shadow-xl rounded-2xl overflow-hidden bg-card">
                <Table>
                  <TableHeader className="bg-muted/30 border-b border-border">
                    <TableRow className="border-border">
                      <TableHead className="font-bold text-muted-foreground">봇 / 종목</TableHead>
                      <TableHead className="font-bold text-muted-foreground">평단가 / 단계</TableHead>
                      <TableHead className="font-bold text-muted-foreground">증거금 / 포지션 / 수량</TableHead>
                      <TableHead className="font-bold text-muted-foreground">실현 PnL / 수수료</TableHead>
                      <TableHead className="font-bold text-muted-foreground">종료 시간</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedClosed.map((cycle) => {
                      const pnl = Number(cycle.pnl || 0);
                      const isLong = (cycle.side || 'long').toLowerCase() === 'long';
                      return (
                        <TableRow key={cycle.id} className="hover:bg-muted/10 transition-colors border-border">
                          <TableCell className="py-4">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-xl ${isLong ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                                {isLong ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                              </div>
                              <div className="flex flex-col">
                                <button
                                  onClick={() => onSelectBot?.(String(cycle.bot_id))}
                                  className="text-left group"
                                >
                                  <div className="font-black text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5 leading-none">
                                    <span className="text-[10px] text-muted-foreground font-bold">#{cycle.bot_id}</span>
                                    <span className={`px-1 rounded text-[8px] uppercase ${getExchangeBadgeStyle(cycle.exchange)} border-none`}>{cycle.exchange || 'binance'}</span>
                                    {cycle.bot_name ? (cycle.bot_name.length > 20 ? cycle.bot_name.substring(0, 20) + '...' : cycle.bot_name) : cycle.bot_symbol}
                                  </div>
                                </button>
                                <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-tight mt-0.5 flex items-center gap-1.5">
                                  <div className="flex items-center gap-1">
                                    {!cycle.is_live ? (
                                      <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[8px] h-3.5 px-1 leading-none font-black">PAPER</Badge>
                                    ) : cycle.use_testnet ? (
                                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[8px] h-3.5 px-1 leading-none font-black">TESTNET</Badge>
                                    ) : (
                                      <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[8px] h-3.5 px-1 leading-none font-black">LIVE</Badge>
                                    )}
                                  </div>
                                  {cycle.strategy_name || 'DCA 전략'}
                                </div>
                                <p className="text-[10px] text-muted-foreground font-medium opacity-60">{cycle.bot_symbol}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                             <p className="text-xs font-bold text-foreground">${formatPrice(cycle.avg_price || 0)}</p>
                            <div className="flex items-center gap-1 mt-1">
                              <Badge className="bg-primary/10 text-primary border-none font-bold h-3.5 text-[8px] px-1">
                                {cycle.current_step}단계
                              </Badge>
                              {cycle.exit_type === 'tp' && <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[8px] h-3.5 px-1 font-black">익절</Badge>}
                              {cycle.exit_type === 'sl' && <Badge variant="outline" className="bg-danger/10 text-danger border-danger/20 text-[8px] h-3.5 px-1 font-black">손절</Badge>}
                              {cycle.exit_type === 'manual' && <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-[8px] h-3.5 px-1 font-black">수동</Badge>}
                              {cycle.exit_type === 'offset' && <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[8px] h-3.5 px-1 font-black">상계</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <p className="text-xs font-black text-foreground">{isPrivate ? '****' : `$${formatPrice(cycle.total_cost || 0, 2)}`} <span className="text-[10px] font-bold text-muted-foreground">Margin</span></p>
                              <p className="text-[11px] font-bold text-primary">{isPrivate ? '****' : `$${formatPrice((cycle.total_cost || 0) * (cycle.leverage || 1), 2)}`} <span className="text-[9px] text-muted-foreground opacity-60">Notional</span></p>
                              <p className="text-[10px] text-muted-foreground font-medium">{isPrivate ? '****' : formatQty(cycle.total_qty || 0, 6)} Qty</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <p className={`text-xs font-black ${cycle.exit_type === 'offset' ? 'text-amber-500' : (pnl >= 0 ? 'text-success' : 'text-danger')}`}>
                                {isPrivate ? '****' : `${pnl >= 0 ? '+' : ''}${formatPrice(pnl, 4)} USDT`}
                              </p>
                              <p className={`text-[10px] font-bold ${cycle.exit_type === 'offset' ? 'text-amber-500/80' : (pnl >= 0 ? 'text-success/80' : 'text-danger/80')}`}>
                                ROE: {isPrivate ? '****' : `${pnl >= 0 ? '+' : ''}${formatPrice(cycle.roe || (pnl / (cycle.total_cost || 1)) * 100, 2)}%`}
                              </p>
                              <p className="text-[10px] text-muted-foreground mt-0.5">Fee: {isPrivate ? '****' : `$${formatPrice((cycle.total_cost || 0) * (cycle.leverage || 1) * 0.0008, 4)}`}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-xs text-muted-foreground">
                              {fmtDateTime(cycle.closed_at)}
                            </p>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>

              {historiesTotal > historiesPer && (
                <div className="flex items-center justify-between px-6 py-4 bg-muted/10 rounded-2xl mt-4 border border-border">
                  <div className="text-[11px] text-muted-foreground font-black uppercase tracking-widest pl-2">
                    총 {historiesTotal.toLocaleString()}건 중 {(historiesPage - 1) * historiesPer + 1}~{Math.min(historiesPage * historiesPer, historiesTotal)} 표시
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newPage = Math.max(1, historiesPage - 1);
                        setHistoriesPage(newPage);
                        fetchData(newPage);
                      }}
                      disabled={historiesPage === 1 || loading}
                      className="h-9 w-9 p-0 rounded-xl hover:bg-primary/10 hover:text-primary transition-all shadow-none border-border"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center justify-center h-9 px-4 bg-background border border-border rounded-xl text-xs font-black text-primary min-w-[80px]">
                      {historiesPage} / {Math.ceil(historiesTotal / historiesPer)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newPage = historiesPage + 1;
                        setHistoriesPage(newPage);
                        fetchData(newPage);
                      }}
                      disabled={historiesPage >= Math.ceil(historiesTotal / historiesPer) || loading}
                      className="h-9 w-9 p-0 rounded-xl hover:bg-primary/10 hover:text-primary transition-all shadow-none border-border"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
      {/* 내보내기 다이얼로그 */}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-sm rounded-[32px] shadow-2xl border-none bg-card overflow-hidden">
            <CardHeader className="pb-4">
              <div className="flex justify-between items-center mb-1">
                <Badge variant="outline" className="text-[10px] font-black px-2 py-0.5 rounded-lg border-none bg-primary/10 text-primary uppercase">
                  CSV EXPORT
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => setIsExportModalOpen(false)} className="h-8 w-8 p-0 rounded-full hover:bg-muted"><X className="w-4 h-4" /></Button>
              </div>
              <CardTitle className="text-xl font-black flex items-center gap-2">
                데이터 내보내기
              </CardTitle>
              <CardDescription className="text-xs font-medium">선택한 기간의 체결 이력을 CSV 파일로 저장합니다.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <label className="text-[11px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> 기간 선택
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={exportYear} onValueChange={setExportYear}>
                    <SelectTrigger className="rounded-xl border-border bg-muted/20 font-bold h-11">
                      <SelectValue placeholder="연도" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체 연도</SelectItem>
                      {["2024", "2025", "2026"].map(y => (
                        <SelectItem key={y} value={y}>{y}년</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={exportMonth} onValueChange={setExportMonth} disabled={exportYear === 'all'}>
                    <SelectTrigger className="rounded-xl border-border bg-muted/20 font-bold h-11">
                      <SelectValue placeholder="월" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체 월</SelectItem>
                      {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                        <SelectItem key={m} value={m}>{m}월</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-[11px] font-bold text-muted-foreground px-1">
                  <span>모드</span>
                  <span className="text-foreground">{mode === 'all' ? '전체' : (mode === 'mainnet' ? '실전' : (mode === 'testnet' ? '테스트넷' : '시뮬레이션'))}</span>
                </div>
                <div className="flex justify-between text-[11px] font-bold text-muted-foreground px-1">
                  <span>봇</span>
                  <span className="text-foreground">{selectedBot === 'all' ? '전체 봇' : `Bot #${selectedBot}`}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex gap-3 px-6 pb-6 pt-0">
              <Button
                onClick={() => {
                  const url = `/dca/export_histories?mode=${mode}&bot_id=${selectedBot}&year=${exportYear}&month=${exportMonth}`;
                  window.location.href = url;
                  setIsExportModalOpen(false);
                }}
                className="flex-1 h-12 rounded-2xl font-black bg-primary text-white hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
              >
                다운로드 시작
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
};
